import { createContext, type ReactNode, useContext } from "react";
import {
  detachedSettingsPath,
  type SettingsSlug,
  type StandingAnswerFocus,
  settingsSectionFor,
  standingAnswerQuery,
} from "../src/settings-sections.ts";
import { slugOfWorkspace } from "./workspace-slug.ts";

/*
 * The workspace the rep is looking at (docs/2026-09-19-workspace-scope-critique.md). It comes from
 * the URL, never from a plan, so every request names the workspace on screen and the server refuses
 * anything that belongs to another one.
 */
export interface CurrentWorkspace {
  readonly id: string;
  readonly name: string;
}

export const WorkspaceContext = createContext<CurrentWorkspace | null>(null);

export function useWorkspace(): CurrentWorkspace {
  const workspace = useContext(WorkspaceContext);
  if (!workspace) throw new Error("A workspace view must sit inside WorkspaceContext");
  return workspace;
}

export const workspaceApi = (workspaceId: string): string =>
  `/api/work/workspaces/${encodeURIComponent(workspaceId)}`;

/** The API path of one plan inside the workspace on screen. */
export function usePlanApi(planId: string): string {
  return `${workspaceApi(useWorkspace().id)}/plans/${encodeURIComponent(planId)}`;
}

/** A workspace's address names its slug (`workspace-slug.ts`); `workspaceId` is always the id. */
export const inboxPath = (workspaceId: string): string =>
  `/w/${encodeURIComponent(slugOfWorkspace(workspaceId))}`;
/**
 * One conversation, opened: the unified inbox's `?c=` where the workspace has the inbox, else the
 * old inbox's `?plan=`, which only that screen reads.
 */
export const conversationPath = (workspaceId: string, planId: string, inbox: boolean): string =>
  `${inboxPath(workspaceId)}?${inbox ? "c" : "plan"}=${encodeURIComponent(planId)}`;
export const settingsPath = (workspaceId: string): string => `${inboxPath(workspaceId)}/settings`;
/**
 * One section of the settings area. `settingsPath` lands on the default section. An agency or
 * account section opens at its own address, which names no workspace (LEA-501).
 */
export const settingsSectionPath = (workspaceId: string, slug: SettingsSlug): string => {
  const section = settingsSectionFor(slug);
  return (section && detachedSettingsPath(section)) ?? `${settingsPath(workspaceId)}/${slug}`;
};
/** Standing answers is a resource the rep maintains, not a setting, so it has its own path. */
export const answersPath = (workspaceId: string): string => `${inboxPath(workspaceId)}/answers`;
/** The workspace's Bison sending mailboxes, an admin page of their own (LEA-290). */
export const mailboxesPath = (workspaceId: string): string => `${inboxPath(workspaceId)}/mailboxes`;
/** A campaign is a resource with its own page, so the list of them is a resource too. */
export const campaignsPath = (workspaceId: string, search = ""): string =>
  `${inboxPath(workspaceId)}/campaigns${search}`;
export const campaignPath = (workspaceId: string, campaignId: string): string =>
  `${campaignsPath(workspaceId)}/${encodeURIComponent(campaignId)}`;
/** Standing answers, opened on one fact, optionally scoped to one campaign. */
export const answersFocusPath = (workspaceId: string, focus: StandingAnswerFocus): string =>
  `${answersPath(workspaceId)}?${standingAnswerQuery(focus)}`;

/*
 * How a link inside the app moves, from wherever it is rendered. The workspace view provides the
 * move that asks before discarding an unsaved draft; outside one, a link is an ordinary page load.
 */
export const NavigateContext = createContext<(href: string) => void>((href) => {
  location.assign(href);
});

/** A link that moves in place, and still opens a new tab on a modified click as any link does. */
export function AppLink(props: {
  readonly href: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const navigate = useContext(NavigateContext);
  return (
    <a
      href={props.href}
      className={props.className}
      onClick={(event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
        event.preventDefault();
        navigate(props.href);
      }}
    >
      {props.children}
    </a>
  );
}
